import { useMutation } from 'react-query';
import usePlanStore, { CountryFormState } from 'store/planStore';
import { Countries } from 'shared/enums';
import moment from 'moment/moment';
import { savePlan } from '../../../../../../api/services/mediaPlanning/savePlan';
import { useError } from '../../../common/Errors/ErrorContext/ErrorContext';

const useSavePlan = () => {
  const {
    generalInfoState,
    selectedCountryISOCode,
    setAlreadySavedPlanId,
    storedCountryForms,
  } = usePlanStore();

  const { showError } = useError();
  const convertDatesToISO = (
    form: Partial<CountryFormState>
  ): Partial<CountryFormState> => {
    const convert = (date?: string | null) =>
      date ? moment(date, 'DD-MM-YYYY').format('YYYY-MM-DD') : date;

    return {
      ...form,
      startDate: convert(form.startDate) || '',
      offline: {
        ...form.offline,
        tv: form.offline?.tv
          ? {
              ...form.offline?.tv,
              startDate: moment(form.offline.tv.startDate, 'DD-MM-YYYY').format(
                'YYYY-MM-DD'
              ),
              endDate: moment(form.offline.tv.endDate, 'DD-MM-YYYY').format(
                'YYYY-MM-DD'
              ),
            }
          : undefined,
      },
      youTubeConfig: form.youTubeConfig && {
        ...form.youTubeConfig,
        ...(process.env.REACT_APP_API_VERSION !== 'v3' && {
          skipShare: form.youTubeConfig?.skipValue,
          unskipShare: form.youTubeConfig?.unSkipValue,
        }),
      },
      metaConfig: form.metaConfig
        ? {
            ...form.metaConfig,
            // to omit migrations on back.
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            startDate: convert(form.metaConfig.startDate),
          }
        : undefined,
    };
  };

  const convertCountryFormsToISO = (): Record<
    string,
    Partial<CountryFormState>
  > => {
    return Object.fromEntries(
      Object.entries(storedCountryForms).map(([country, form]) => [
        country,
        {
          ...convertDatesToISO(form),
          calculatedBudget: form.calculatedBudget ?? [],
        },
      ])
    );
  };

  const countrySetupWithISO = convertCountryFormsToISO();

  const {
    mutate: saveMediaPlan,
    mutateAsync: asyncSaveMediaPlan,
    data: response,
    isLoading,
    isError,
  } = useMutation(
    async () => {
      return savePlan({
        body: {
          campaignName: generalInfoState.campaignName,
          division: generalInfoState.division,
          brand: generalInfoState.brand,
          category: generalInfoState.category,
          countrySetup: countrySetupWithISO,
        },
        countryISO: selectedCountryISOCode as Countries,
      });
    },
    {
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message || error.message || 'Unknown error';
        showError(errorMessage);
      },
      onSuccess: (res) => {
        const savedId = res.data.id;

        setAlreadySavedPlanId(savedId);
      },
    }
  );

  return {
    saveMediaPlan,
    asyncSaveMediaPlan,
    data: response?.data,
    isLoading,
    isError,
  };
};

export default useSavePlan;
